import { useCallback, useState } from 'react'

import dayjs from 'dayjs'
import {
  Activity,
  BarChart3,
  Clock,
  LayoutDashboard,
  TrendingUp,
} from 'lucide-react'
import type { JobListingType } from 'types/graphql'

import { navigate } from '@redwoodjs/router'
import { Metadata, useQuery } from '@redwoodjs/web'

import { PageHeaderLayout } from 'src/components/Core/PageHeaderLayout'
import { RefreshButton } from 'src/components/Core/RefreshButton'
import {
  SegmentedTabs,
  type SegmentedTabItem,
} from 'src/components/Core/SegmentedTabs'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_PIPELINE_HERO_STATS } from 'src/graphql/statusLogs.graphql'
import { ApplicantStage } from 'src/graphql/types/applicants'
import { parseDateRangeFromUrl } from 'src/lib/url-date-range.utils'
import {
  getCommaSeparatedSearchParam,
  getSearchParam,
  setTabSearchParam,
} from 'src/lib/url-search-params.utils'

import { HeroStatCard } from '../components/HeroStatCard'
import { buildDelta, formatCount, round1 } from '../components/heroStatHelpers'
import FiltersBar from '../FiltersBar'

import { buildApplicantsFilterUrl } from './buildApplicantsFilterUrl'
import ExportAnalyticsButton from './components/ExportAnalyticsButton'
import MarketingTab from './MarketingTab/MarketingTab'
import PipelineActivityContainer from './OverviewTab/PipelineActivityContainer'
import PipelineStagesByJobListingCard from './OverviewTab/PipelineStagesByJobListingCard'
import PipelineSummaryContainer from './OverviewTab/PipelineSummaryContainer'
import PerformanceTab from './PerformanceTab/PerformanceTab'
import RetentionTab from './RetentionTab/RetentionTab'

type AnalyticsTab =
  | 'overview'
  | 'marketing'
  | 'performance'
  | 'health'
  | 'retention'

const ANALYTICS_TABS = [
  { value: 'overview', label: 'Overview', icon: LayoutDashboard },
  { value: 'marketing', label: 'Marketing', icon: BarChart3 },
  { value: 'performance', label: 'Performance', icon: Clock },
  { value: 'health', label: 'Health', icon: Activity },
  { value: 'retention', label: 'Retention', icon: TrendingUp },
] as const satisfies ReadonlyArray<SegmentedTabItem<AnalyticsTab>>

// Health is scaffolded but not yet shipped — keep its render branch and the
// union type intact, but hide it from the tab strip until its content is built
// out.
const VISIBLE_TABS: ReadonlyArray<SegmentedTabItem<AnalyticsTab>> =
  ANALYTICS_TABS.filter((t) => t.value !== 'health')

// Guards against garbage in the ?tab= URL param so a typo can't put us in an
// undefined state. Hidden tabs (health) are still accepted by the type guard
// so deep links keep working until/unless those tabs are fully removed.
const isAnalyticsTab = (s: string | null): s is AnalyticsTab =>
  s != null && ANALYTICS_TABS.some((t) => t.value === s)

const ComingSoon = ({ title, body }: { title: string; body: string }) => (
  <div className="rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
    <h3 className="text-base font-semibold text-doubleNickel-gray-900">
      {title}
    </h3>
    <p className="mx-auto mt-2 max-w-xl text-sm text-doubleNickel-gray-500">
      {body}
    </p>
  </div>
)

const AnalyticsPageV2 = () => {
  // Mirror the URL-param state read by the existing AnalyticsPage so admins can
  // paste a filtered URL and get the same filters applied here.
  const selectedRecruitersFromUrl = getCommaSeparatedSearchParam('recruiters')
  const selectedLeadSourcesFromUrl = getCommaSeparatedSearchParam('leadSources')
  const selectedJobListingsFromUrl = getCommaSeparatedSearchParam('jobListings')
  const selectedJobTypesFromUrl = getCommaSeparatedSearchParam('jobTypes')

  const dateRange = parseDateRangeFromUrl(getSearchParam('dateRange')) ?? [
    dayjs().subtract(30, 'day').startOf('day').toDate(),
    new Date(),
  ]

  // Tab is URL-persisted so refresh / shared links land on the right view.
  // Falls back to 'overview' for missing or unknown values.
  const tabUrlString = getSearchParam('tab')
  const initialTab: AnalyticsTab = isAnalyticsTab(tabUrlString)
    ? tabUrlString
    : 'overview'

  const [selectedRecruiters, setSelectedRecruiters] = useState(
    selectedRecruitersFromUrl
  )
  // Encoded lead-source filter values: bare LinkType ("RECRUITER") or
  // "TYPE:name" / "TYPE:__NO_NAME__" (see src/lib/leadSource.utils.ts).
  const [selectedLeadSources, setSelectedLeadSources] = useState<string[]>(
    selectedLeadSourcesFromUrl
  )
  const [selectedJobListings, setSelectedJobListings] = useState(
    selectedJobListingsFromUrl
  )
  const [selectedJobTypes, setSelectedJobTypes] = useState<JobListingType[]>(
    selectedJobTypesFromUrl as JobListingType[]
  )
  const [startDate, setStartDate] = useState(dateRange[0])
  const [endDate, setEndDate] = useState(dateRange[1])
  const [activeTab, setActiveTab] = useState<AnalyticsTab>(initialTab)

  const handleTabChange = useCallback((value: AnalyticsTab) => {
    setActiveTab(value)
    setTabSearchParam(value)
  }, [])
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null)
  // Bumped by the Refresh button so child containers (which own their own
  // useQuery) can refetch without us lifting their queries up here.
  const [refreshKey, setRefreshKey] = useState(0)

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const {
    data: heroStatsData,
    loading: heroStatsLoading,
    refetch: refetchHeroStats,
  } = useQuery(GET_PIPELINE_HERO_STATS, {
    variables: {
      filters: {
        recruiters: selectedRecruiters,
        jobListings: selectedJobListings,
        linkTypes: selectedLeadSources,
        jobTypes: selectedJobTypes,
        dateRange: { gte: startDate, lte: endDate },
      },
    },
    onError,
    onCompleted: () => setLastUpdatedAt(new Date()),
    notifyOnNetworkStatusChange: true,
  })

  const heroStats = heroStatsData?.pipelineHeroStats
  const current = heroStats?.current
  const previous = heroStats?.previous

  const leadsDelta = buildDelta({
    current: current?.totalLeads,
    previous: previous?.totalLeads,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: { gte: startDate, lte: endDate },
  })
  const hiredDelta = buildDelta({
    current: current?.totalHired,
    previous: previous?.totalHired,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: { gte: startDate, lte: endDate },
  })
  const cohortHiredDelta = buildDelta({
    current: current?.cohortHired,
    previous: previous?.cohortHired,
    format: 'percent-change',
    unit: 'count',
    currentDateRange: { gte: startDate, lte: endDate },
  })
  const conversionDelta = buildDelta({
    current: current?.conversionRate,
    previous: previous?.conversionRate,
    format: 'pts',
    unit: 'percent',
    currentDateRange: { gte: startDate, lte: endDate },
  })
  const daysToHireDelta = buildDelta({
    current: current?.avgDaysToHire,
    previous: previous?.avgDaysToHire,
    format: 'days',
    lowerIsBetter: true,
    unit: 'days',
    currentDateRange: { gte: startDate, lte: endDate },
  })
  const retentionDelta = buildDelta({
    current: current?.retention,
    previous: previous?.retention,
    format: 'pts',
    unit: 'percent',
    currentDateRange: { gte: startDate, lte: endDate },
  })

  return (
    <PageHeaderLayout
      title="Pipeline Analytics"
      tabs={
        <SegmentedTabs
          tabs={VISIBLE_TABS}
          value={activeTab}
          onChange={handleTabChange}
          ariaLabel="Pipeline analytics sections"
        />
      }
      toolbar={
        <>
          <FiltersBar
            selectedRecruiters={selectedRecruiters}
            setSelectedRecruiters={setSelectedRecruiters}
            selectedJobListings={selectedJobListings}
            setSelectedJobListings={setSelectedJobListings}
            selectedLeadSources={selectedLeadSources}
            setSelectedLeadSources={setSelectedLeadSources}
            selectedJobTypes={selectedJobTypes}
            setSelectedJobTypes={setSelectedJobTypes}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
          />
          <RefreshButton
            onRefresh={() => {
              refetchHeroStats()
              setRefreshKey((k) => k + 1)
            }}
            loading={heroStatsLoading}
            lastUpdatedAt={lastUpdatedAt}
          />
          <ExportAnalyticsButton
            filters={{
              recruiters: selectedRecruiters,
              jobListings: selectedJobListings,
              linkTypes: selectedLeadSources,
              jobTypes: selectedJobTypes,
              dateRange: { gte: startDate, lte: endDate },
            }}
          />
        </>
      }
    >
      <Metadata
        title="Pipeline Analytics"
        description="Pipeline analytics — redesigned"
      />
      {/* Hero KPI strip — shared across overview/marketing/performance/retention
            so users always have the period-level totals as a reference frame
            regardless of which tab they're on. Single useQuery, single fetch. */}
      {activeTab !== 'health' && (
        <div className="mb-4 grid grid-cols-6 gap-3">
          <HeroStatCard
            label="Total Leads"
            value={formatCount(current?.totalLeads)}
            loading={heroStatsLoading}
            loadingShowDelta
            delta={leadsDelta}
            info={
              'All applicants whose application date falls within the selected date range.\n\nIncludes all stages: prospects, hired, not interested, disqualified, and terminated.'
            }
            onClick={() =>
              navigate(
                buildApplicantsFilterUrl({
                  dateRange: { gte: startDate, lte: endDate },
                  recruiterIds: selectedRecruiters,
                  jobListingIds: selectedJobListings,
                  leadSources: selectedLeadSources,
                })
              )
            }
          />
          <HeroStatCard
            label="Total Hires"
            value={formatCount(current?.totalHired)}
            loading={heroStatsLoading}
            loadingShowDelta
            delta={hiredDelta}
            info={
              'Applicants who were hired during the selected date range.\n\nFilters on hire date, not application date — so a hire here may have applied in a prior window. See Cohort Hires for the within-window view.'
            }
            onClick={() =>
              navigate(
                buildApplicantsFilterUrl({
                  stage: ApplicantStage.HIRED,
                  dateRange: { gte: startDate, lte: endDate },
                  recruiterIds: selectedRecruiters,
                  jobListingIds: selectedJobListings,
                  leadSources: selectedLeadSources,
                })
              )
            }
          />
          <HeroStatCard
            label="Cohort Hires"
            value={formatCount(current?.cohortHired)}
            loading={heroStatsLoading}
            loadingShowDelta
            delta={cohortHiredDelta}
            info={
              'Of the applicants who applied in this period, the count who were eventually hired.\n\nStrict subset of Total Leads — pairs with Conversion Rate (Cohort Hires ÷ Total Leads). Recent windows may trend low until the cohort has had time to be hired.'
            }
            onClick={() =>
              navigate(
                buildApplicantsFilterUrl({
                  // cohort-reached: applicationDate in window AND
                  // hiredDate IS NOT NULL. Matches the displayed count
                  // (includes hires that later got terminated, since
                  // hiredDate is still set on those rows).
                  mode: 'cohort-reached',
                  stage: ApplicantStage.HIRED,
                  dateRange: { gte: startDate, lte: endDate },
                  recruiterIds: selectedRecruiters,
                  jobListingIds: selectedJobListings,
                  leadSources: selectedLeadSources,
                })
              )
            }
          />
          <HeroStatCard
            label="Conversion Rate"
            value={round1(current?.conversionRate)}
            suffix="%"
            loading={heroStatsLoading}
            loadingShowDelta
            delta={conversionDelta}
            info={
              'Of the applicants who applied in this period, the fraction eventually hired.\n\nLeads are counted by application date.\n\nRecent windows may trend low until applicants from the cohort have had time to be hired.'
            }
          />
          <HeroStatCard
            label="Time to Hire"
            value={round1(current?.avgDaysToHire)}
            suffix="d"
            loading={heroStatsLoading}
            loadingShowDelta
            delta={daysToHireDelta}
            info={
              'Average number of days from application to hire. Lower is better.'
            }
          />
          <HeroStatCard
            label="Retention"
            value={round1(current?.retention)}
            suffix="%"
            featured
            loading={heroStatsLoading}
            loadingShowDelta
            delta={retentionDelta}
            info={
              'Of applicants hired in this period, the share who were not terminated within 90 days of being hired.\n\nNote: Hires whose 90-day mark has not yet elapsed are counted as retained until they are terminated.'
            }
          />
        </div>
      )}

      {activeTab === 'overview' && (
        <>
          {/* All three Overview cards receive the full FiltersBar facet set
              so their totals stay consistent with each other and with the
              hero KPI strip above. */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(320px,420px)_1fr]">
            <PipelineSummaryContainer
              filters={{
                recruiters: selectedRecruiters,
                jobListings: selectedJobListings,
                linkTypes: selectedLeadSources,
                jobTypes: selectedJobTypes,
                dateRange: { gte: startDate, lte: endDate },
              }}
              refreshKey={refreshKey}
            />
            <PipelineActivityContainer
              filters={{
                recruiters: selectedRecruiters,
                jobListings: selectedJobListings,
                linkTypes: selectedLeadSources,
                jobTypes: selectedJobTypes,
                dateRange: { gte: startDate, lte: endDate },
              }}
              refreshKey={refreshKey}
            />
          </div>
          <div className="mt-3">
            <PipelineStagesByJobListingCard
              filters={{
                recruiters: selectedRecruiters,
                jobListings: selectedJobListings,
                linkTypes: selectedLeadSources,
                jobTypes: selectedJobTypes,
                dateRange: { gte: startDate, lte: endDate },
              }}
              refreshKey={refreshKey}
            />
          </div>
        </>
      )}

      {activeTab === 'marketing' && (
        <MarketingTab
          filters={{
            recruiters: selectedRecruiters,
            jobListings: selectedJobListings,
            linkTypes: selectedLeadSources,
            jobTypes: selectedJobTypes,
            dateRange: { gte: startDate, lte: endDate },
          }}
          refreshKey={refreshKey}
        />
      )}
      {activeTab === 'performance' && (
        <PerformanceTab
          filters={{
            recruiters: selectedRecruiters,
            jobListings: selectedJobListings,
            linkTypes: selectedLeadSources,
            jobTypes: selectedJobTypes,
            dateRange: { gte: startDate, lte: endDate },
          }}
          refreshKey={refreshKey}
        />
      )}
      {activeTab === 'health' && (
        <ComingSoon
          title="Pipeline Health — coming soon"
          body="Stage funnel waterfall, pipeline aging, and disposition reasons."
        />
      )}
      {activeTab === 'retention' && (
        <RetentionTab
          filters={{
            recruiters: selectedRecruiters,
            jobListings: selectedJobListings,
            linkTypes: selectedLeadSources,
            jobTypes: selectedJobTypes,
            dateRange: { gte: startDate, lte: endDate },
          }}
          refreshKey={refreshKey}
        />
      )}
    </PageHeaderLayout>
  )
}

export default AnalyticsPageV2
